<template>
	<div class="ls-sidebar-layout" :class="{ 'is-mobile': isMobile }" :style="cssVariables">
		<div class="navigation-container">
			<div
				class="sidebar-wrapper"
				:class="{
					open: isSidebarOpen,
					'pinned-shut': isUserSidebarLocked === 'pinned-shut',
				}"
			>
				<slot name="sidebar" :is-open="isSidebarOpen" :locked="isUserSidebarLocked" />
			</div>

			<div
				class="content-container"
				:class="{
					'sidebar-open': isSidebarOpen,
					'pinned-shut': isUserSidebarLocked === 'pinned-shut',
				}"
			>
				<slot />
			</div>
		</div>

		<div class="mobile-navbar-wrapper">
			<slot name="mobile-navbar" />
		</div>
	</div>
</template>

<script lang="ts" setup>

type Props = {
	expandedWidth?: string;
	collapsedWidth?: string;
	mobileBreakpoint?: string;
};

const props = withDefaults(defineProps<Props>(), {
	expandedWidth: "200px",
	collapsedWidth: "calc(44px + 2 * 0.8rem)",
	mobileBreakpoint: "900px",
});

const { isSidebarOpen, isUserSidebarLocked, toggleOpen, toggleLock } = useSidebar();

const cssVariables = computed(() => ({
	"--ls-sidebar-expanded-width": props.expandedWidth,
	"--ls-sidebar-collapsed-width": props.collapsedWidth,
}));

const isMobile = ref(false);

onMounted(() => {
	const mediaQuery = window.matchMedia(`(max-width: ${props.mobileBreakpoint})`);
	isMobile.value = mediaQuery.matches;

	const handler = (e: MediaQueryListEvent) => {
		isMobile.value = e.matches;
	};

	mediaQuery.addEventListener("change", handler);
	onUnmounted(() => mediaQuery.removeEventListener("change", handler));
});

defineExpose({
	isSidebarOpen,
	isUserSidebarLocked,
	toggleOpen,
	toggleLock,
});
</script>

<style lang="scss" scoped>
$expanded-width: var(--ls-sidebar-expanded-width, 200px);
$collapsed-width: var(--ls-sidebar-collapsed-width, calc(44px + 2 * 0.8rem));
$transition-timing: cubic-bezier(0.075, 0.82, 0.165, 1);
$transition-duration: 0.35s;

.ls-sidebar-layout {
	display: flex;
	flex-direction: column;
	width: 100%;
	min-height: 100dvh;
}

.navigation-container {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: stretch;
	width: 100%;
	min-height: calc(100dvh - 58px);
	padding: 0;
	margin: 0;
	flex-grow: 1;
}

.sidebar-wrapper {
	width: $collapsed-width;
	min-width: $collapsed-width;
	transition:
		width $transition-duration $transition-timing,
		min-width $transition-duration $transition-timing;

	&.open {
		width: $expanded-width;
		min-width: $expanded-width;
	}

	&.pinned-shut {
		width: $collapsed-width;
		min-width: $collapsed-width;
	}
}

.content-container {
	width: calc(100vw - $collapsed-width);
	min-width: calc(100vw - $collapsed-width);
	max-width: calc(100vw - $collapsed-width);
	transition:
		width $transition-duration $transition-timing,
		min-width $transition-duration $transition-timing,
		max-width $transition-duration $transition-timing;

	&.sidebar-open {
		width: calc(100vw - $expanded-width);
		min-width: calc(100vw - $expanded-width);
		max-width: calc(100vw - $expanded-width);
	}

	&.pinned-shut {
		width: calc(100vw - $collapsed-width);
		min-width: calc(100vw - $collapsed-width);
		max-width: calc(100vw - $collapsed-width);
	}
}

.mobile-navbar-wrapper {
	display: none;
}

.ls-sidebar-layout.is-mobile {
	.sidebar-wrapper {
		width: 0;
		min-width: 0;

		&.open {
			width: 0;
			min-width: 0;
		}
	}

	.sidebar-wrapper > :deep(*) {
		display: none;
	}

	.mobile-navbar-wrapper {
		display: flex;
	}

	.content-container {
		width: 100vw;
		min-width: 100vw;
		max-width: 100vw;

		&.sidebar-open {
			width: 100vw;
			min-width: 100vw;
			max-width: 100vw;
		}
	}
}

// Pre-hydration: CSS media query provides correct initial layout
// before JS sets the .is-mobile class, preventing FOUC
@media (max-width: 900px) {
	.ls-sidebar-layout:not(.is-mobile) {
		.sidebar-wrapper {
			width: 0;
			min-width: 0;

			&.open {
				width: 0;
				min-width: 0;
			}
		}

		.sidebar-wrapper > :deep(*) {
			display: none;
		}

		.mobile-navbar-wrapper {
			display: flex;
		}

		.content-container {
			width: 100vw;
			min-width: 100vw;
			max-width: 100vw;

			&.sidebar-open {
				width: 100vw;
				min-width: 100vw;
				max-width: 100vw;
			}
		}
	}
}
</style>
